// Each MessageRow's per-row props, built from a session's state: the plugin
// fold, the artifact lists per message, the tool-call maps, all cut to the
// row's own slice by RowPropsCache (rowProps.ts) so the row memo holds.
//
// Moved out of AgentChat so the side pane's conversation (SidePaneChat's
// `conversation` input, Douglas plan T8) draws the agent's output the same way
// from the same session state, with no second copy of these rules.

import { useMemo, useRef } from 'react'
import type { AgentMessage, ArtifactInfo, AskUserQuestionInfo, CreatedDashboardInfo, RenderedChartInfo, RenderedTableInfo, AgentSSEEvent as CoreSSEEvent } from '../types.js'
import type { RenderPlugin } from '../plugins.js'
import { PluginFold, RowPropsCache, type RowSlice } from './rowProps.js'

export interface RowSliceInputs {
  /** The conversation: what the artifact lists are matched against. */
  messages: AgentMessage[]
  /** What is drawn, one slice each: the conversation plus any queued messages. Default: `messages`. */
  displayMessages?: AgentMessage[]
  artifacts: ArtifactInfo[]
  renderedTables: ReadonlyMap<string, RenderedTableInfo>
  renderedCharts: ReadonlyMap<string, RenderedChartInfo>
  askedQuestions: ReadonlyMap<string, AskUserQuestionInfo>
  createdDashboards: ReadonlyMap<string, CreatedDashboardInfo>
  plugins: RenderPlugin[]
  pluginEvents: CoreSSEEvent[]
  isStreaming: boolean
}

const REGISTER_ARTIFACT_TOOLS = new Set(['register_artifact', 'mcp__ui__register_artifact', 'mcp__data__register_artifact'])

export function useRowSlices({
  messages, displayMessages = messages, artifacts, renderedTables, renderedCharts,
  askedQuestions, createdDashboards, plugins, pluginEvents, isStreaming,
}: RowSliceInputs): RowSlice[] {
  // Fold plugin events into per-plugin, per-toolCallId state.
  // Pure — replay-safe. Keyed by plugin index so two plugins sharing an
  // event type do not collide. Depends on plugins identity + pluginEvents array.
  // Incremental: a live plugin event folds only itself, so every other tool
  // call's state keeps its identity and its row stays put (rowProps.ts).
  const pluginFoldRef = useRef<PluginFold | null>(null)
  if (!pluginFoldRef.current) pluginFoldRef.current = new PluginFold()
  const byPlugin = pluginFoldRef.current.fold(plugins, pluginEvents)

  // Build two maps of artifacts per message
  const { autoArtifacts, toolArtifacts } = useMemo(() => {
    const autoMap = new Map<string, ArtifactInfo[]>()
    const toolMap = new Map<string, ArtifactInfo[]>()
    const claimedPaths = new Set<string>()

    for (let i = messages.length - 1; i >= 0; i--) {
      const message = messages[i]
      const toolPaths: string[] = []
      const autoPaths: string[] = []

      if (message.toolCalls) {
        for (const tc of message.toolCalls) {
          if (REGISTER_ARTIFACT_TOOLS.has(tc.name)) {
            const filePath = tc.input?.file_path as string | undefined
            if (filePath && !claimedPaths.has(filePath)) toolPaths.push(filePath)
          }
        }
      }

      if (message.autoArtifactPaths) {
        for (const p of message.autoArtifactPaths) {
          if (!claimedPaths.has(p)) autoPaths.push(p)
        }
      }

      for (const p of [...toolPaths, ...autoPaths]) claimedPaths.add(p)

      if (toolPaths.length > 0) {
        const matched = artifacts.filter(a => toolPaths.some(p => a.filePath === p || a.filePath.endsWith(p)))
        if (matched.length > 0) toolMap.set(message.id, matched)
      }
      if (autoPaths.length > 0) {
        const matched = artifacts.filter(a =>
          a.artifactType !== 'webapp' &&
          autoPaths.some(p => a.filePath === p || a.filePath.endsWith(p))
        )
        if (matched.length > 0) autoMap.set(message.id, matched)
      }
    }
    return { autoArtifacts: autoMap, toolArtifacts: toolMap }
  }, [messages, artifacts])

  // Build maps of tool call IDs to rendered tables/charts
  const toolCallTables = useMemo(() => {
    const map = new Map<string, RenderedTableInfo[]>()
    for (const table of renderedTables.values()) {
      if (table.toolCallId) {
        const existing = map.get(table.toolCallId) || []
        existing.push(table)
        map.set(table.toolCallId, existing)
      }
    }
    return map
  }, [renderedTables])

  const toolCallCharts = useMemo(() => {
    const map = new Map<string, RenderedChartInfo[]>()
    for (const chart of renderedCharts.values()) {
      if (chart.toolCallId) {
        const existing = map.get(chart.toolCallId) || []
        existing.push(chart)
        map.set(chart.toolCallId, existing)
      }
    }
    return map
  }, [renderedCharts])

  const toolCallQuestions = useMemo(() => {
    const map = new Map<string, AskUserQuestionInfo>()
    for (const q of askedQuestions.values()) {
      if (q.toolCallId) map.set(q.toolCallId, q)
    }
    return map
  }, [askedQuestions])

  const toolCallDashboards = useMemo(() => {
    const map = new Map<string, CreatedDashboardInfo>()
    for (const dash of createdDashboards.values()) {
      if (dash.toolCallId) map.set(dash.toolCallId, dash)
    }
    return map
  }, [createdDashboards])

  // Each row's own slice of the maps above, reusing last render's objects
  // wherever the contents are unchanged — so a streamed delta, a plugin event
  // or isStreaming flipping re-renders only the rows it actually concerns
  // (rowProps.ts). Computed in render: a pure function of these inputs, with
  // the cache only remembering what it handed out last time.
  const rowPropsCacheRef = useRef<RowPropsCache | null>(null)
  if (!rowPropsCacheRef.current) rowPropsCacheRef.current = new RowPropsCache()
  return rowPropsCacheRef.current.build(displayMessages, {
    autoArtifacts,
    toolArtifacts,
    byPlugin,
    toolCallTables,
    toolCallCharts,
    toolCallQuestions,
    toolCallDashboards,
    isStreaming,
  })
}
